import { getRelativeTime } from '@thoughtspot/date-format-util';
import { i18nKeys, translate as t } from '@thoughtspot/i18n';
import { Button } from '@thoughtspot/radiant-react/widgets/button/button';
import { ElementType } from '@thoughtspot/radiant-react/widgets/form-builder/interface';
import { IconColor, IconID } from '@thoughtspot/radiant-react/widgets/icon';
import {
    ColumnWidth,
    ColumnWidthType,
} from '@thoughtspot/radiant-react/widgets/table/object-table/object-table.interface';
import { getUserGuid } from '@thoughtspot/session-service';
import React from 'react';
import { parseNumber } from '/@utils/pinboard-styles/pinboard-style.util';
import styles from './collections.container.module.scss';
import { StyleConstants, TranslationsKeys } from './constants';
import { MoreOptions } from './more-options/more-options';
import { PrimaryColumn } from './primary-column/primary-column.container';

export enum CellType {
    'link' = 0,
    'text' = 1,
    'avatar' = 2,
    'checkbox' = 3,
    'radio' = 4,
    'menu' = 5,
    'hoverAction' = 6,
    'empty' = 7,
    'details' = 8,
    'tag' = 9,
    'button' = 10,
    'dropdown' = 11,
}

// Updated ListActionType with the new RESET_PASSWORD action
export enum ListActionType {
    ADD_TO_COLLECTION = 'add',
    MOVE_THIS_COLLECTION = 'move',
    DELETE = 'delete',
    REMOVE = 'remove',
    SHARE = 'share',
    OPEN = 'open',
}

export const FeaturePaths = {
    callosumBasePath: '/callosum/v1',
};

export enum tableColumnHeaders {
    COLLECTION_NAME = 'name',
    COLLECTION_DESCRIPTION = 'description',
    MORE_OPTION = 'moreOptions',
    CREATED = 'created',
    AUTHOR = 'author',
    TYPE = 'type',
    EDITED = 'updated',
}

export const transformData = (folderMetadata: any): any => {
    return {
        ...folderMetadata,
        primary: {
            primaryText: folderMetadata.name,
            secondaryText: folderMetadata.description,
        },
        author: [
            {
                disabled: false,
                id: folderMetadata.author,
                isNameLink: true,
                name: folderMetadata.authorName,
                src: `${FeaturePaths.callosumBasePath}/image/profile/${folderMetadata.author}`,
            },
        ],
        created: {
            disabled: false,
            text: getRelativeTime(
                parseNumber(0, folderMetadata.createdAt),
                false,
            ),
        },
        updated: {
            disabled: false,
            text: getRelativeTime(
                parseNumber(0, folderMetadata.updatedAt),
                false,
            ),
        },
    };
};

export const transformCollectionsData = (collectionsData: any) => {
    return collectionsData.map((collection: any) => transformData(collection));
};

export const FolderNameElement: React.FC<any> = ({ data, context }) => {
    const name = data[tableColumnHeaders.COLLECTION_NAME];
    const description = data[tableColumnHeaders.COLLECTION_DESCRIPTION];
    return (
        <PrimaryColumn
            primaryText={name}
            secondaryText={description}
            handleClick={() => {
                context.onRowAction({ action: ListActionType.OPEN, data });
            }}
        />
    );
};

export const generateMoreOption = (context: any, data: any) => {
    const shareOption = {
        id: ListActionType.SHARE,
        label: t(TranslationsKeys.MORE_ACTIONS_SHARE),
        onClick: () => {
            context.onRowAction({
                action: ListActionType.SHARE,
                data,
            });
        },
    };
    const addToCollection = {
        id: ListActionType.ADD_TO_COLLECTION,
        label: t('collection.addCollection.label'),
        onClick: () => {
            context.onRowAction({
                action: ListActionType.ADD_TO_COLLECTION,
                data,
            });
        },
    };
    const moveToCollection = {
        id: ListActionType.MOVE_THIS_COLLECTION,
        label: t('collection.moveCollection.label'),
        onClick: () => {
            context.onRowAction({
                action: ListActionType.MOVE_THIS_COLLECTION,
                data,
            });
        },
    };
    const deleteOption = {
        title: 'Group2',
        items: [
            {
                id: ListActionType.DELETE,
                label: t(TranslationsKeys.MORE_ACTIONS_DELETE),
                onClick: () => {
                    context.onRowAction({
                        action: ListActionType.DELETE,
                        data,
                    });
                },
            },
        ],
    };
    if (
        data.permissions.metadata === 'MODIFY' ||
        data?.author?.id === getUserGuid()
    ) {
        const options = {
            title: 'Group1',
            items: [shareOption, moveToCollection, addToCollection],
        };
        return [options, deleteOption];
    }
    const options = {
        title: 'Group1',
        items: [shareOption, moveToCollection],
    };
    return [options];
};

export const getColumnDefinitions = () => {
    return [
        {
            cellRendererFramework: (params: any) => {
                return <FolderNameElement {...params} />;
            },
            headerName: t(TranslationsKeys.COLLECTION_NAME_COLUMN),
            headerClass: [styles.headerStyle],
            colId: tableColumnHeaders.COLLECTION_NAME,
            suppressMovable: true,
            sortable: true,
            flex: 2,
            columnWidthType: ColumnWidthType.Fluid,
            columnWidth: ColumnWidth.Large,
        },
        {
            cellRenderer: CellType[CellType.avatar],
            headerClass: [styles.headerStyle],
            headerName: t(TranslationsKeys.COLLECTIONS_AUTHOR_COLUMN),
            colId: tableColumnHeaders.AUTHOR,
            sortable: true,
            flex: 2,
            suppressMovable: true,
        },
        {
            cellRenderer: CellType[CellType.text],
            headerClass: [styles.headerStyle],
            headerName: t(TranslationsKeys.COLLECTIONS_CREATED_COLUMN),
            colId: tableColumnHeaders.CREATED,
            sortable: true,
            flex: 2,
            suppressMovable: true,
        },
        {
            cellRenderer: CellType[CellType.text],
            headerClass: [styles.headerStyle],
            headerName: t(TranslationsKeys.COLLECTION_UPDATED_COLUMN),
            colId: tableColumnHeaders.EDITED,
            sortable: true,
            initialSort: 'desc',
            flex: 2,
            suppressMovable: true,
        },
        {
            cellRenderer: (params: any) => {
                const { context, data } = params;
                return (
                    <MoreOptions
                        menuId={1}
                        menuLabel={t(
                            TranslationsKeys.COLLECTION_MORE_OPTIONS_COLUMN,
                        )}
                        menuItemGroups={generateMoreOption(context, data)}
                        isDisabled={data.isChecked}
                    />
                );
            },
            colId: tableColumnHeaders.MORE_OPTION,
            headerClass: [styles.moreOptionsHeaderStyle],
            sortable: false,
            width: 50,
            suppressMovable: true,
        },
    ];
};

export const getAddEditOrgElementsConfig = (
    translate: (key: i18nKeys, args?: any) => string,
    required = true,
): any[] => {
    return [
        {
            id: 'name',
            elementType: ElementType.INPUT,
            config: {
                label: translate(TranslationsKeys.MODAL_NAME_LABEL),
                type: 'text',
                wrapContent: true,
            },
            validation: {
                required,
                requiredError: translate(TranslationsKeys.MODAL_ERROR, {
                    field_name: translate(TranslationsKeys.MODAL_NAME_LABEL),
                }),
                maxLength: 200,
                maxLengthError: translate(TranslationsKeys.MODAL_LENGTH_ERORR, {
                    field_name: translate(TranslationsKeys.MODAL_NAME_LABEL),
                    max_length: 200,
                }),
            },
        },
        {
            id: 'description',
            elementType: ElementType.INPUT,
            config: {
                label: translate(TranslationsKeys.MODAL_DESCRIPTION_LABEL),
                type: 'textarea',
                wrapContent: true,
                placeholder: t('collection.dialog.create.desc.placeholder'),
            },
            validation: {
                maxLength: 500,
                maxLengthError: translate(TranslationsKeys.MODAL_LENGTH_ERORR, {
                    field_name: translate(
                        TranslationsKeys.MODAL_DESCRIPTION_LABEL,
                    ),
                    max_length: 500,
                }),
            },
        },
    ];
};

export const renderActionsBtnNoIcon = (
    text: string,
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void,
    isPrimaryBtn = false,
    className: string,
) => (
    <Button
        text={text}
        onClick={onClick}
        className={className}
        type={
            isPrimaryBtn
                ? StyleConstants.PRIMARY_BUTTON
                : StyleConstants.SECONDARY_BUTTON
        }
    />
);

// render action button
export const renderActionsBtn = (
    text: string,
    iconId: IconID,
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void,
    isPrimaryBtn = false,
    className: string,
    iconColor: IconColor = IconColor.GRAY,
) => {
    return (
        <Button
            text={text}
            onClick={onClick}
            iconColor={iconColor}
            icon={iconId}
            size={!text || text.length === 0 ? 's' : 'm'}
            className={className && className.length !== 0 ? className : null}
            type={
                isPrimaryBtn
                    ? StyleConstants.PRIMARY_BUTTON
                    : StyleConstants.SECONDARY_BUTTON
            }
        />
    );
};

// render action button
export const renderActionsBtnM = (
    text: string,
    iconId: IconID,
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void,
    isPrimaryBtn = false,
    className: string,
    iconColor: IconColor = IconColor.GRAY,
    disabled = false,
) => {
    return (
        <Button
            text={text}
            onClick={onClick}
            iconColor={iconColor}
            icon={iconId}
            size="m"
            isDisabled={disabled}
            className={className && className.length !== 0 ? className : null}
            type={
                isPrimaryBtn
                    ? StyleConstants.PRIMARY_BUTTON
                    : StyleConstants.SECONDARY_BUTTON
            }
        />
    );
};

export const getEffectivePermissions = (data: any[], ids: any[]) => {
    let metadataPermission = 'MODIFY';
    let contentPermission = 'MODIFY';
    ids.forEach(id => {
        const obj = data.find(ob => ob.id === id);
        if (obj) {
            const permmission = obj.permissions;
            if (permmission.metadata === 'READ_ONLY') {
                metadataPermission = 'READ_ONLY';
            }
            if (permmission.content === 'READ_ONLY') {
                contentPermission = 'READ_ONLY';
            }
        }
    });
    return [metadataPermission, contentPermission];
};
